import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { COVERLY_WHATSAPP } from '@/mocks/coverly';

interface Pago {
  id: string; mes: string; monto: number;
  fecha: string | null; estado: string;
  metodo: string; referencia_oxxo: string | null;
}

interface Props {
  user: { nombre: string; folio: string; planPrecio: number; metodo_pago?: string };
  memberId: string;
}

export default function ScreenPagos({ user, memberId }: Props) {
  const [pagos, setPagos] = useState<Pago[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!memberId || memberId === 'admin') { setLoading(false); return; }
    supabase.from('pagos').select('*').eq('member_id', memberId)
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        if (data) setPagos(data.map(p => ({
          id: p.id, mes: p.mes || '', monto: p.monto,
          fecha: p.fecha_pago, estado: p.estado,
          metodo: p.metodo, referencia_oxxo: p.referencia_oxxo,
        })));
        setLoading(false);
      });
  }, [memberId]);

  const pendiente = pagos.find(p => p.estado === 'pendiente');

  return (
    <div className="min-h-screen bg-[#F7F6FF] px-5 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#0d0520]">Mis Pagos</h1>
        <p className="text-sm text-gray-500 mt-1">Historial de tu membresía</p>
      </div>

      {pendiente && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-5">
          <div className="flex items-start gap-3">
            <i className="ri-error-warning-line text-amber-500 text-xl mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-amber-800 text-sm">Pago pendiente — ${pendiente.monto} MXN</p>
              {pendiente.referencia_oxxo ? (
                <>
                  <p className="text-amber-700 text-xs mt-1">Referencia OXXO:</p>
                  <p className="font-mono font-bold text-amber-800 text-base tracking-wider">{pendiente.referencia_oxxo}</p>
                  <p className="text-amber-600 text-xs mt-1">Paga en OXXO, 7-Eleven o Farmacias del Ahorro</p>
                </>
              ) : (
                <p className="text-amber-700 text-xs mt-1">Contáctanos para activar tu membresía</p>
              )}
              <a href={`https://wa.me/${COVERLY_WHATSAPP}`}
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-white px-3 py-2 rounded-full"
                style={{ backgroundColor: '#25D366' }}>
                <i className="ri-whatsapp-line" /> Pagar por WhatsApp
              </a>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#534AB7] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : pagos.length === 0 ? (
        <div className="bg-white rounded-2xl p-8 text-center border border-gray-100">
          <i className="ri-receipt-line text-4xl text-gray-300 mb-3 block" />
          <p className="text-gray-500 text-sm">No hay pagos registrados aún.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {pagos.filter(p => p.estado !== 'pendiente').map(p => (
            <div key={p.id} className="bg-white rounded-2xl p-4 border border-gray-100 flex items-center justify-between">
              <div>
                <p className="font-semibold text-[#0d0520] text-sm">{p.mes}</p>
                <p className="text-gray-400 text-xs">{p.fecha || 'Fecha pendiente'}</p>
              </div>
              <div className="text-right">
                <p className="font-bold text-[#0d0520]">${p.monto} MXN</p>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  p.estado === 'pagado' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}>{p.estado === 'pagado' ? 'Pagado' : 'Fallido'}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
